import {useMemo, useState} from 'react';

export type SortDir = 'asc' | 'desc';

/**
 * Client-side column sort for the roster tables. The whole roster is already loaded (see
 * usePeopleRoster), so sorting happens in memory rather than via a server refetch.
 *
 * `getValue` must be stable (define it at module scope) so the sort doesn't recompute every render.
 */
export const useClientSort = <Row>(
  rows: Row[],
  getValue: (row: Row, key: string) => string,
  initialKey: string,
  initialDir: SortDir = 'asc'
) => {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState<SortDir>(initialDir);

  const toggleSort = (key: string) => {
    if (key === sortKey) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  };

  const sorted = useMemo(() => {
    const next = [...rows].sort((a, b) =>
      getValue(a, sortKey).localeCompare(getValue(b, sortKey), undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
    return sortDir === 'desc' ? next.reverse() : next;
  }, [rows, getValue, sortKey, sortDir]);

  return {sorted, sortKey, sortDir, toggleSort};
};
